import {
  Avatar,
  Drawer,
  IconButton,
  Tab,
  Tabs,
  withStyles,
  TabsProps,
} from "@material-ui/core"
import { FileType } from "models/documents"
import { css, cx } from "@emotion/css"
import { format, parseISO } from "date-fns"
import { useState } from "react"
import { CancellationData as CancellationDataType } from "models/bookingOrdersCancellation"
import CloseIcon from "@material-ui/icons/Close"
import { Item } from "components/Documents"
import Skeleton from "@material-ui/lab/Skeleton"
import { downloadDocument } from "api/library"
import { getBookingOrderCancellationDetails } from "api/bookingOrder"
import { useI18n } from "providers/I18n"
import { useQuery } from "react-query"

const CustomTab = withStyles({
  root: {
    textTransform: "none",
  },
  selected: {
    fontWeight: "bold",
    borderBottomColor: "#00B1A4",
  },
})(Tab)

function ViewCancellationDetailsModal(props: any) {
  /**
   * Hooks
   */
  const { t } = useI18n()
  const [activeTab, setActiveTab] = useState("details")
  const { bookingOrderId } = props

  /**
   * Returns file type based on complete name
   * @param fileName
   * @returns
   */
  const extractFileType = (fileName: string) => {
    return fileName.split(".").slice(-1)[0] as FileType
  }

  /**
   * Cancellation details to back-end
   */
  const { data: cancellationData, isLoading } = useQuery(
    ["bo-cancellation-details", bookingOrderId],
    getBookingOrderCancellationDetails,
    {
      onError: () => {
        resetModalData()
      },
      enabled: !!bookingOrderId,
    },
  )

  const getFile = () => {
    if (!cancellationData) return []

    const { cancelBy, cancelDate, cancelDocumentName, cancelDocumentId } =
      cancellationData
    return [
      {
        id: cancelDocumentId,
        fileType: extractFileType(cancelDocumentName),
        name: cancelDocumentName,
        ownerEntityId: bookingOrderId,
        dateCreated: parseISO(cancelDate).getTime(),
        createdByDetails: {
          username: cancelBy,
          email: "",
          firstName: "",
          lastName: "",
        },
      },
    ]
  }

  /**
   * Returns a key index or default value from cancellationData
   */
  const getCancellationIndex = (key: string, defaultValue: any) => {
    if (!cancellationData) return defaultValue

    const parsedKey = key as keyof CancellationDataType
    return cancellationData[parsedKey] || defaultValue
  }

  /**
   * Reset cancellation data
   */
  const resetModalData = () => {
    setActiveTab("details")
  }

  /**
   * Returns avatar text
   * @returns {string}
   */
  const getAvatarText = (): string => {
    return getCancellationIndex("cancelBy", "")
      .split(" ")
      .slice(0, 2)
      .map((item: string[]) => item[0])
      .join("")
  }

  const styledTabsProps: TabsProps = {
    classes: {
      flexContainer: styles.tabUnderline,
    },
    indicatorColor: "primary",
  }

  return (
    <Drawer
      variant="persistent"
      anchor="right"
      open={props.open}
      classes={{
        paperAnchorRight: styles.drawer,
      }}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <h5 className="BodyBody-1BlackBold">{t("bo.cancel.details")}</h5>
          <IconButton onClick={props.onClose} color="inherit">
            <CloseIcon />
          </IconButton>
        </div>
        <div>
          <Tabs
            value={activeTab}
            onChange={(_event, value) => {
              setActiveTab(value)
            }}
            className={styles.tabs}
            {...styledTabsProps}
          >
            <CustomTab label={t("bo.cancel.details.details")} value="details" />
            <CustomTab
              label={t("bo.cancel.details.comments")}
              value="comments"
            />
            <CustomTab label={t("bo.cancel.details.files")} value="files" />
          </Tabs>

          {isLoading && <Skeleton variant="rect" height={120} />}

          {!isLoading && activeTab === "details" && (
            <div>
              <p>
                <strong>{t("bo.cancel.details.details")}</strong>
              </p>

              <div className={styles.detailsContainer}>
                <p>{t("bo.cancel.details.cancelled.by")}</p>
                <p>
                  <strong>{getCancellationIndex("cancelBy", "")}</strong>
                </p>

                <p>{t("bo.cancel.details.cancelled.date")}</p>
                {!!cancellationData?.cancelDate && (
                  <p>
                    {format(
                      parseISO(
                        getCancellationIndex(
                          "cancelDate",
                          new Date().getTime(),
                        ),
                      ),
                      "dd/MM/yyyy",
                    )}
                  </p>
                )}

                <p>{t("bo.cancel.details.cancelled.time")}</p>
                {!!cancellationData?.cancelDate && (
                  <p>
                    {format(
                      parseISO(
                        getCancellationIndex(
                          "cancelDate",
                          new Date().getTime(),
                        ),
                      ),
                      "HH:mm:ss",
                    )}
                  </p>
                )}
              </div>
            </div>
          )}

          {!isLoading && activeTab === "comments" && (
            <div>
              <div className={styles.avatarContainer}>
                <Avatar
                  className={cx(styles.avatar, "BodyBody-sWhiteRegular")}
                  children={getAvatarText()}
                />
                <div>
                  <p>
                    <strong>{getCancellationIndex("cancelBy", "")}</strong>
                  </p>
                  <span>{getCancellationIndex("cancelComment", "")}</span>
                </div>
              </div>
            </div>
          )}

          {!isLoading && activeTab === "files" && (
            <div>
              {getFile() &&
                getFile().map((file) => (
                  <Item
                    disabled
                    key={file?.id}
                    document={file}
                    type="campaign"
                    onDownload={downloadDocument}
                    className={styles.fileItem}
                  />
                ))}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  )
}

export default ViewCancellationDetailsModal

const styles = {
  drawer: css`
    width: fit-content;
    display: flex;
    align-content: start;
    border: 0;
    box-shadow: 0 0 18px 0 rgba(0, 0, 0, 0.1);
    justify-content: space-between;
  `,
  container: css`
    padding: var(--spacing-l) var(--spacing-xxl-2);
  `,
  tabs: css`
    margin-bottom: var(--spacing-xxl-2);
    border-bottom: 1px solid #e7e6e6;
  `,
  header: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-xxl-2);
  `,
  avatar: css`
    width: 32px;
    height: 32px;
  `,
  avatarContainer: css`
    background-color: var(--light-grey-2);
    padding: var(--spacing-xs) var(--spacing-m);
    display: grid;
    grid-template-columns: 50px auto;
    border-radius: 3px;
    font-size: 0.9rem;
  `,
  fileItem: css`
    max-width: 600px;
    padding: var(--spacing-m);
  `,
  detailsContainer: css`
    display: grid;
    width: 100%;
    grid-template-columns: 1fr 1fr;
    row-gap: var(--spacing-xs);
    margin-top: var(--spacing-xxl-2);
    font-size: 0.9rem;
  `,
  tabUnderline: css`
    border-bottom: 1px solid var(--light-grey);
    min-width: fit-content;
  `,
}
